import {
  DatePickerType,
  DatePicker as MantineDatePicker,
  DatePickerProps as MantineDatePickerProps,
} from '@mantine/dates';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { getNowAsDate } from '@zorro/shared/formatters';

import styles from './DatePicker.module.css';

/**
 * Header chevron sizing.
 *
 * Figma draws the chevrons at 20px with a `border/1_5` variable in scope, but
 * Tabler strokes at 1.5 read noticeably thinner in the browser than they do on
 * the Figma canvas. Per `FIGMA_TO_CODE_PATTERNS.md` we use stroke 2 for visual
 * parity. Approved 2026-09-15.
 */
const CHEVRON_SIZE = 20;
const CHEVRON_STROKE = 2;

/**
 * Props for the V2 inline calendar panel.
 *
 * `size` is intentionally omitted: Figma pins the day cell at 40px, which is
 * not on Mantine's size ladder (30/36/42/48/54). Omitting the prop turns a
 * stale `size="xl"` into a compile error rather than a silent no-op.
 *
 * `unstyled` is omitted because the V2 styling only holds together with
 * Mantine's own CSS out of the way — the component always sets it.
 *
 * `withWeekNumbers` is omitted at the designer's request: week numbers are not
 * part of the V2 calendar. Removed 2026-09-15.
 */
export type DatePickerProps<T extends DatePickerType = 'default'> = Omit<
  MantineDatePickerProps<T>,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'unstyled'
  | 'withWeekNumbers'
>;

/**
 * True when nothing at all was supplied for the selection, so we may seed it.
 *
 * `defaultValue` is read by Mantine on the first render only, so a session
 * left open across midnight keeps the date it mounted with — the same
 * behaviour as any other uncontrolled default.
 */
const defaultsToToday = <T extends DatePickerType>(
  props: Partial<DatePickerProps<T>>
) =>
  (props.type ?? 'default') === 'default' &&
  props.value === undefined &&
  props.defaultValue === undefined;

/**
 * Today, typed as whatever `defaultValue` this `T` expects.
 *
 * The cast is safe because the only caller guards on `defaultsToToday`, which
 * is true for `type="default"` alone — and there `defaultValue` is
 * `string | Date | null`. TypeScript cannot narrow the generic `T` from a
 * runtime check on `props.type`, hence the assertion.
 */
const today = <T extends DatePickerType>() =>
  getNowAsDate() as unknown as MantineDatePickerProps<T>['defaultValue'];

/**
 * The V2 calendar look, for Mantine components that embed a calendar in their
 * dropdown (`DateInput`, `DatePickerInput`, `MonthPickerInput`).
 *
 * Figma models "Two Columns" and "Show Outside Dates" as independent booleans.
 * Mantine instead computes `hideOutsideDates ?? numberOfColumns !== 1`, which
 * silently hides outside dates in multi-column calendars, so it is pinned.
 *
 * Mantine's default chevrons are unusable under `unstyled`: their left/right
 * rotation lives only in the stripped class, so both arrows render as the same
 * glyph. They also write an inline `width: 45%`, which makes any CSS width
 * rule on `calendarHeaderControlIcon` dead code.
 */
export const calendarPanelProps = {
  hideOutsideDates: false,
  previousIcon: <IconChevronLeft size={CHEVRON_SIZE} stroke={CHEVRON_STROKE} />,
  nextIcon: <IconChevronRight size={CHEVRON_SIZE} stroke={CHEVRON_STROKE} />,
};

export const calendarPanelClassNames = styles;

/**
 * V2 inline calendar panel — pick a single date, a range, or multiple
 * non-contiguous dates.
 *
 * This is the bare panel as drawn in Figma: no border and no shadow. Anything
 * mounting it inside a popover owns that chrome.
 *
 * ## Value contract
 *
 * Mantine v9 is string-based. `value`/`defaultValue` accept `string | Date`,
 * but **`onChange` always hands back `'YYYY-MM-DD'` strings** — a range as
 * `[string | null, string | null]`, multiple as `string[]`. Every callback
 * (`getDayProps`, `renderDay`, `excludeDate`, the function form of
 * `monthLabelFormat`) receives strings too. Convert at the call site if you
 * need `Date`.
 *
 * ## Default selection
 *
 * A `type="default"` picker mounted with neither `value` nor `defaultValue`
 * opens with **today** selected. Pass `defaultValue={null}` to open empty, or
 * control the picker and hold `null` in your own state. `range` and `multiple`
 * always open empty.
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState<string | null>(null);
 * <DatePicker value={value} onChange={setValue} />
 * ```
 *
 * @example Range
 * ```tsx
 * <DatePicker type="range" value={range} onChange={setRange} />
 * ```
 */
export const DatePicker = <T extends DatePickerType = 'default'>(
  props: DatePickerProps<T>
) => (
  <MantineDatePicker
    unstyled
    classNames={calendarPanelClassNames}
    // A picker that was given no value at all opens on today rather than
    // empty (designer request, 2026-09-15). Only the uncontrolled single-date
    // case is seeded: a controlled `value` — including `null` — belongs to the
    // consumer, and seeding `range`/`multiple` would open in a half-finished
    // selection. Mantine normalises the `Date` with local-time `dayjs`, so
    // this is today in the user's timezone, not UTC.
    defaultValue={defaultsToToday(props) ? today<T>() : undefined}
    {...calendarPanelProps}
    {...props}
  />
);

export { type DatePickerType } from '@mantine/dates';
